import { useEffect, useRef } from "react";
import { Star, ArrowRight } from "lucide-react";
import { SOCIAL_LINKS } from "@/lib/social";

const ELFSIGHT_WIDGET_ID = process.env.REACT_APP_ELFSIGHT_REVIEWS_ID || "";

/**
 * Live Google Reviews carousel powered by Elfsight (free tier).
 *
 * Setup (~5 min):
 *  1. Go to https://elfsight.com/google-reviews-widget/
 *  2. Sign up (free), create a Google Reviews widget, paste your Google Maps URL.
 *  3. Copy the widget ID (looks like "1a2b3c4d-5678-90ef-1234-567890abcdef").
 *  4. Set REACT_APP_ELFSIGHT_REVIEWS_ID in /app/frontend/.env to that ID and restart frontend.
 *
 * Until then, this section shows a clean placeholder with a "Read reviews on Google" CTA.
 */
export default function GoogleReviewsWidget() {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!ELFSIGHT_WIDGET_ID) return;
    // Load Elfsight platform script once
    if (!document.querySelector("script[data-elfsight-platform]")) {
      const s = document.createElement("script");
      s.src = "https://static.elfsight.com/platform/platform.js";
      s.async = true;
      s.dataset.elfsightPlatform = "true";
      document.body.appendChild(s);
    }
  }, []);

  return (
    <section data-testid="google-reviews-widget" className="py-20 md:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mb-10">
          <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-[#0055FF] mb-3 block">Real Reviews</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A0A0A] tracking-tight mb-4">
            What Dublin Says About Us
          </h2>
          <p className="text-[#475569] text-base md:text-lg leading-relaxed">
            Live, verified reviews from our Google Business Profile. Updated automatically.
          </p>
        </div>

        {ELFSIGHT_WIDGET_ID ? (
          <div
            ref={containerRef}
            className={`elfsight-app-${ELFSIGHT_WIDGET_ID}`}
            data-elfsight-app-lazy
          />
        ) : (
          <div data-testid="google-reviews-fallback" className="bg-white border border-black/10 p-8 sm:p-10 rounded-sm">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-white border border-black/10 rounded-full flex items-center justify-center shrink-0" aria-hidden>
                <svg viewBox="0 0 24 24" className="w-6 h-6" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.28 1.42-1.07 2.62-2.28 3.42v2.84h3.69c2.16-1.99 3.61-4.93 3.61-8.5z"/>
                  <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.69-2.84c-1.02.69-2.32 1.09-4.24 1.09-3.26 0-6.02-2.2-7.01-5.16H1.18v3.24C3.15 21.31 7.27 24 12 24z"/>
                  <path fill="#FBBC05" d="M4.99 14.18A7.2 7.2 0 0 1 4.62 12c0-.76.13-1.49.37-2.18V6.58H1.18A11.99 11.99 0 0 0 0 12c0 1.93.46 3.76 1.18 5.42l3.81-3.24z"/>
                  <path fill="#EA4335" d="M12 4.84c1.77 0 3.35.61 4.6 1.81l3.27-3.27C17.95 1.5 15.24.5 12 .5 7.27.5 3.15 3.19 1.18 6.58l3.81 3.24C5.98 7.04 8.74 4.84 12 4.84z"/>
                </svg>
              </div>
              <div>
                <h3 className="font-extrabold text-lg text-[#0A0A0A] mb-1">Verified by Google</h3>
                <p className="text-sm text-[#475569]">Every review on our profile is written by a real Dublin customer and verified by Google. No fake testimonials, ever.</p>
              </div>
            </div>
            <div className="flex gap-1 mb-1">
              {[1,2,3,4,5].map((s) => <Star key={s} className="w-5 h-5 text-yellow-400 fill-yellow-400" />)}
            </div>
            <p className="text-xs text-[#475569] mb-6">Real ratings on our Google Business Profile</p>
            <a
              href={SOCIAL_LINKS.googleReview}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-fallback-cta"
              className="inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#0044CC] text-white font-bold px-6 py-3.5 rounded-sm text-base transition-colors"
            >
              Read Reviews on Google <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
